import React, { useState, useEffect, useCallback } from 'react';
import { timeTrackingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Clock, Play, Square, Coffee, Timer } from 'lucide-react';

export const TimeTrackingWidget = () => {
  const [timeStatus, setTimeStatus] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadTimeStatus = useCallback(async () => {
    try {
      const response = await timeTrackingAPI.getStatus();
      setTimeStatus(response.data);
    } catch (error) {
      console.error('Failed to load time status:', error);
    }
  }, []);

  useEffect(() => {
    loadTimeStatus();
    const interval = setInterval(loadTimeStatus, 60000);
    return () => clearInterval(interval);
  }, [loadTimeStatus]);

  const handleQuickAction = async (action) => {
    setActionLoading(true);
    try {
      const actions = {
        'clock-in': { fn: timeTrackingAPI.clockIn, msg: 'Eingestempelt' },
        'clock-out': { fn: timeTrackingAPI.clockOut, msg: 'Ausgestempelt' },
        'break-start': { fn: timeTrackingAPI.breakStart, msg: 'Pause gestartet' },
        'break-end': { fn: timeTrackingAPI.breakEnd, msg: 'Pause beendet' },
      };
      const { fn, msg } = actions[action];
      await fn();
      toast.success(msg);
      loadTimeStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Aktion');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="px-4 pb-2">
      <div className="p-3 rounded-lg bg-muted/30 border border-border/40">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Zeiterfassung</span>
          </div>
          {timeStatus && (
            <Badge
              variant={timeStatus.status === 'clocked_in' ? 'default' : timeStatus.status === 'on_break' ? 'secondary' : 'outline'}
              className="text-xs"
            >
              {timeStatus.status === 'clocked_in' ? 'Aktiv' :
               timeStatus.status === 'on_break' ? 'Pause' :
               timeStatus.status === 'clocked_out' ? 'Beendet' : 'Nicht eingestempelt'}
            </Badge>
          )}
        </div>

        {timeStatus?.status === 'clocked_in' && timeStatus?.working_time && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
            <Timer className="h-3 w-3" />
            <span>Heute: {Math.floor(timeStatus.working_time / 60)}:{(timeStatus.working_time % 60).toString().padStart(2, '0')} Std</span>
          </div>
        )}

        <div className="flex gap-1">
          {(!timeStatus || timeStatus.status === 'not_clocked_in') && (
            <Button size="sm" className="flex-1 h-7 text-xs" onClick={() => handleQuickAction('clock-in')} disabled={actionLoading}>
              <Play className="h-3 w-3 mr-1" /> Kommen
            </Button>
          )}
          {timeStatus?.status === 'clocked_in' && (
            <>
              <Button size="sm" variant="secondary" className="flex-1 h-7 text-xs" onClick={() => handleQuickAction('break-start')} disabled={actionLoading}>
                <Coffee className="h-3 w-3 mr-1" /> Pause
              </Button>
              <Button size="sm" variant="destructive" className="flex-1 h-7 text-xs" onClick={() => handleQuickAction('clock-out')} disabled={actionLoading}>
                <Square className="h-3 w-3 mr-1" /> Gehen
              </Button>
            </>
          )}
          {timeStatus?.status === 'on_break' && (
            <Button size="sm" className="flex-1 h-7 text-xs" onClick={() => handleQuickAction('break-end')} disabled={actionLoading}>
              <Play className="h-3 w-3 mr-1" /> Pause beenden
            </Button>
          )}
          {timeStatus?.status === 'clocked_out' && (
            <div className="flex-1 text-center text-xs text-muted-foreground py-1">Arbeitstag beendet</div>
          )}
        </div>
      </div>
    </div>
  );
};
